import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// PROMPT 13M — staff-only top-bar document triage popover. Reuses GET /doc-center/preset-counts (13L).
const ROWS = [
  { key: "waiting", label: "Waiting for review" },
  { key: "clarification", label: "Needs clarification" },
  { key: "stale_signing", label: "Stale signing" },
  { key: "stale_pro", label: "Stale professional reviews" },
  { key: "stale_client", label: "Stale client uploads" },
  { key: "stale_vendor", label: "Stale vendor / partner uploads" },
  { key: "never", label: "Never reminded" },
  { key: "recently", label: "Recently reminded" },
];

// PROMPT 13N — oldest-item age wording for a triage row.
const fmtAge = (d) => d == null ? "" : d <= 0 ? "oldest today" : `oldest ${d}d`;

export function DocTriageMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [ages, setAges] = useState({});
  const [thresholds, setThresholds] = useState({ warning_days: 7, urgent_days: 14 });
  const [denied, setDenied] = useState(false);
  const [open, setOpen] = useState(false);
  const isPortal = ["client", "partner_vendor"].includes(user?.role);

  useEffect(() => {
    if (isPortal) return;
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/doc-center/preset-counts"); if (alive) { setCounts(data.counts || {}); setAges(data.ages || {}); if (data.thresholds) setThresholds(data.thresholds); setDenied(false); } }
      catch (e) { if (alive && e?.response?.status === 403) setDenied(true); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isPortal]);

  if (isPortal || denied) return null;
  const c = counts || {};
  const waiting = c.waiting || 0;
  const go = (key) => { setOpen(false); navigate(`/documents/review?preset=${key}`); };

  // PROMPT 13O/13P — urgency color from the oldest item age across presets, using admin-configured thresholds.
  const warnT = Number(thresholds?.warning_days) || 7;
  const urgT = Number(thresholds?.urgent_days) || 14;
  const ageVals = Object.values(ages || {}).filter((v) => v != null);
  const maxAge = ageVals.length ? Math.max(...ageVals) : null;
  const urgency = maxAge == null ? "none" : maxAge >= urgT ? "urgent" : maxAge >= warnT ? "warning" : "normal";
  const badgeBg = urgency === "urgent" ? "bg-[#9C1E22]" : urgency === "warning" ? "bg-[#B45309]" : "bg-[#1C4358]";
  const oldestLabel = maxAge == null ? null : maxAge <= 0 ? "today" : `${maxAge} day${maxAge === 1 ? "" : "s"}`;
  const ariaLabel = oldestLabel ? `Documents needing review — oldest document item: ${oldestLabel}` : "Documents needing review";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="topbar-doc-triage"
          data-urgency={urgency}
          title={ariaLabel}
          aria-label={ariaLabel}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]"
        >
          <Icons.ClipboardCheck size={19} />
          {waiting > 0 && (
            <span data-testid="topbar-doc-triage-count" className={`absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full ${badgeBg} text-white text-[10px] font-bold flex items-center justify-center`}>
              {waiting > 99 ? "99+" : waiting}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="doc-triage-menu">
        <div className="px-3 py-2.5 border-b border-[#EEF2F5] flex items-center gap-2">
          <Icons.FileCheck2 size={15} className="text-[#1C4358]" />
          <span className="font-display font-semibold text-[13px] text-[#1C4358]">Document Review</span>
        </div>
        <div className="py-1">
          {ROWS.map((r) => {
            const n = c[r.key] ?? 0;
            const age = ages[r.key];
            const tone = n > 0 && age != null && age >= urgT ? "text-[#9C1E22] font-semibold"
              : n > 0 && age != null && age >= warnT ? "text-[#B45309]"
              : "text-[#94A3B8]";
            return (
              <button key={r.key} data-testid={`triage-row-${r.key}`} onClick={() => go(r.key)}
                className="w-full flex items-center justify-between gap-3 px-3 py-1.5 text-[12.5px] text-[#252221] hover:bg-[#F4F8FB] transition-colors">
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate">{r.label}</span>
                  {n > 0 && age != null && (
                    <span data-testid={`triage-age-${r.key}`} className={`block text-[10.5px] leading-tight ${tone}`}>{fmtAge(age)}</span>
                  )}
                </span>
                <span className={`shrink-0 min-w-[20px] text-center text-[11px] font-semibold rounded-full px-1.5 ${n > 0 ? "bg-[#1C4358]/10 text-[#1C4358]" : "bg-[#EEF2F5] text-[#94A3B8]"}`}>{n}</span>
              </button>
            );
          })}
        </div>
        <button data-testid="triage-view-all" onClick={() => { setOpen(false); navigate("/documents/review"); }}
          className="w-full flex items-center justify-center gap-1 px-3 py-2 text-[12px] font-medium text-[#1C4358] border-t border-[#EEF2F5] hover:bg-[#F4F8FB]">
          View all <Icons.ArrowRight size={12} />
        </button>
      </PopoverContent>
    </Popover>
  );
}
